import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, createContext, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocaleCodes, SITE_LOCALES } from '@medable/i18n-supported-locales';
import { Locale } from 'libs.i18n-core';
import { LocaleContext, ModalWindow, SetLocaleContext, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { formatLocaleOption, LocalesDropdown } from 'libs.react_internal';
import { updateMyAccountDetails } from '../../helpers/axon_user.helpers';
import { getUserLocaleLocalStorageKey } from '../../helpers/local_storage.helpers';
import { isPseudoLocale, toCortexLocale } from '../../helpers/pseudo_locale.helpers';
import { getStoredPseudoLocale, setStoredPseudoLocale } from '../../helpers/pseudo_locale_storage.helpers';
import { getStatLocaleOverride } from '../../helpers/stat_locale_override.helpers';
import { resolveStudyLocale } from '../../helpers/study_locale.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { AxonAccountContext } from '../axon_account';
import { SiteContext } from '../site';
const includesLocale = (locales, locale) => locales.some((supportedLocale) => toCortexLocale(supportedLocale) === toCortexLocale(locale));
//  Dedupe by normalized locale, keeping the first occurrence, so format variants (e.g. `en-US` and `en_US`) don't repeat.
const dedupeLocales = (locales) => {
    const seen = new Set();
    return locales.filter((locale) => {
        const key = toCortexLocale(locale);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};
const waitForLocaleReloadBoundary = () => new Promise((resolve) => setTimeout(resolve, 0));
const getSupportedLocale = (locales, locale) => locales.find((supportedLocale) => !!locale && toCortexLocale(supportedLocale) === toCortexLocale(locale));
export const UserLocaleContext = createContext({
    isUserLocaleChecked: false,
    siteLocales: [],
    canChangeSiteLocale: false,
    isGlobalLocaleSupported: false,
    openLocaleModal: () => { },
    getUserSelectedLocale: () => '',
});
export const UserLocaleProvider = ({ children, rootShellLocale, setShellLocale, }) => {
    const { environment } = useCurrentEnvironment();
    const currentLocale = useContext(LocaleContext);
    const { site } = useContext(SiteContext);
    //  English (en-US) is the base locale and is always offered as a selectable option for site users. Only prepend it
    //  when the site doesn't already support it (in any format) so we keep the site's own value and avoid duplicates.
    const configuredLocales = useMemo(() => dedupeLocales(site?.c_site_supported_locales ?? [...SITE_LOCALES]), [site]);
    const baseSiteLocalesList = useMemo(() => includesLocale(configuredLocales, LocaleCodes.en_US)
        ? configuredLocales
        : [LocaleCodes.en_US, ...configuredLocales], [configuredLocales]);
    const siteLocalesList = baseSiteLocalesList;
    const navigate = useNavigate();
    const [isLocaleModalOpen, setIsLocaleModalOpen, setIsLocaleModalClosed] = useBooleanState();
    const [selectedLocale, setSelectedLocale] = useState();
    const [isUserLocaleChecked, setIsUserLocaleChecked] = useState(false);
    const [isLoading, setIsLoading, setIsNotLoading] = useBooleanState();
    const { account: axonUser, setAccount: setAxonAccount } = useContext(AxonAccountContext);
    const translate = useLocalizeMessage();
    const setLocale = useContext(SetLocaleContext);
    // SetLocaleContext's value is a fresh closure each render; keep it in a ref so the unmount cleanup
    // below can call the latest one without re-subscribing the effect.
    const setLocaleRef = useRef(setLocale);
    setLocaleRef.current = setLocale;
    const setShellLocaleRef = useRef(setShellLocale);
    setShellLocaleRef.current = setShellLocale;
    // Keep the global preference separate from the shell's current display locale. A study-specific
    // choice temporarily drives both visible contexts but must never become the preference input.
    const rootShellLocaleRef = useRef(rootShellLocale ?? currentLocale);
    const axonLocaleOverrideRef = useRef();
    const pendingAxonLocaleRef = useRef();
    const localeChangeSequenceRef = useRef(0);
    const axonLocaleScopeKey = `${environment?.id ?? ''}:${site?._id ?? ''}:${axonUser?._id ?? ''}`;
    const effectiveAxonLocale = axonLocaleOverrideRef.current?.scopeKey === axonLocaleScopeKey
        ? axonLocaleOverrideRef.current.locale
        : axonUser?.locale;
    const sessionLocale = axonLocaleOverrideRef.current?.scopeKey === axonLocaleScopeKey ? axonLocaleOverrideRef.current.locale : undefined;
    if (rootShellLocale) {
        rootShellLocaleRef.current = rootShellLocale;
    }
    const userLocaleKey = getUserLocaleLocalStorageKey({
        environmentId: environment?.id || 0,
        siteId: site?._id || '',
        axonUserId: axonUser?._id || '',
    });
    const storedPseudoLocale = getStoredPseudoLocale();
    const storedStatLocaleOverride = getStatLocaleOverride();
    const activeStatLocaleOverride = storedStatLocaleOverride &&
        !isPseudoLocale(storedStatLocaleOverride) &&
        getSupportedLocale(siteLocalesList, storedStatLocaleOverride)
        ? storedStatLocaleOverride
        : undefined;
    const globalLocale = rootShellLocaleRef.current;
    const isGlobalLocaleSupported = !isPseudoLocale(globalLocale) && !!getSupportedLocale(siteLocalesList, globalLocale);
    const canChangeSiteLocale = siteLocalesList.length > 1 &&
        !storedPseudoLocale &&
        !activeStatLocaleOverride &&
        !isPseudoLocale(globalLocale) &&
        !isGlobalLocaleSupported;
    const setUserLocale = async (locale, persistAxonLocale = false) => {
        if (!environment || !axonUser) {
            return;
        }
        if (!isPseudoLocale(locale) && !getSupportedLocale(siteLocalesList, locale)) {
            openLocaleModal();
            return;
        }
        const cortexLocale = toCortexLocale(locale);
        const existingOverride = axonLocaleOverrideRef.current;
        const pendingAxonLocale = pendingAxonLocaleRef.current;
        if (pendingAxonLocale?.scopeKey === axonLocaleScopeKey &&
            toCortexLocale(pendingAxonLocale.locale) === cortexLocale) {
            return;
        }
        // An automatic check may have been scheduled by a render that predates the user's picker
        // selection. Never let that stale work supersede the locale the user just chose.
        if (!persistAxonLocale &&
            !isPseudoLocale(locale) &&
            existingOverride?.scopeKey === axonLocaleScopeKey &&
            toCortexLocale(existingOverride.locale) !== cortexLocale) {
            return;
        }
        const localeChangeSequence = ++localeChangeSequenceRef.current;
        const previousOverride = existingOverride;
        const effectiveLocaleAtStart = previousOverride?.scopeKey === axonLocaleScopeKey ? previousOverride.locale : axonUser.locale;
        const shouldPersistAxonLocale = persistAxonLocale &&
            !isPseudoLocale(locale) &&
            (!effectiveLocaleAtStart || toCortexLocale(effectiveLocaleAtStart) !== cortexLocale);
        if (persistAxonLocale && !isPseudoLocale(locale)) {
            // Publish the intended Axon value before awaiting the PUT. Effects can rerun while the request
            // is in flight; they must resolve against this value instead of the stale account context.
            axonLocaleOverrideRef.current = { scopeKey: axonLocaleScopeKey, locale: cortexLocale };
        }
        if (shouldPersistAxonLocale) {
            pendingAxonLocaleRef.current = {
                scopeKey: axonLocaleScopeKey,
                locale: cortexLocale,
                sequence: localeChangeSequence,
            };
        }
        setIsLoading();
        try {
            // Pseudo locale updates skip API calls, so yield once to let the loading/unmount boundary render.
            await waitForLocaleReloadBoundary();
            if (isPseudoLocale(locale)) {
                setStoredPseudoLocale(locale);
            }
            else {
                // The global preference remains owned by IAM. Axon stores only an explicit compatibility
                // choice made in the site picker; supported globals and single-locale fallbacks are transient.
                setStoredPseudoLocale(null);
                localStorage.removeItem(userLocaleKey);
                if (shouldPersistAxonLocale) {
                    const updatedAxonUser = await updateMyAccountDetails({
                        environment,
                        accountId: axonUser?._id,
                        payload: { locale: cortexLocale },
                    });
                    setAxonAccount?.(updatedAxonUser);
                    if (pendingAxonLocaleRef.current?.sequence === localeChangeSequence) {
                        pendingAxonLocaleRef.current = undefined;
                    }
                }
            }
            // A newer selection owns the UI even if this older request completes afterward.
            if (localeChangeSequence !== localeChangeSequenceRef.current) {
                return;
            }
            const localeToApply = Locale[toCortexLocale(locale)];
            setLocale?.(localeToApply);
            setShellLocaleRef.current?.(localeToApply);
            setSelectedLocale(formatLocaleOption(locale, locale));
            setIsUserLocaleChecked(true);
        }
        catch (error) {
            if (localeChangeSequence !== localeChangeSequenceRef.current) {
                return;
            }
            axonLocaleOverrideRef.current = previousOverride;
            if (pendingAxonLocaleRef.current?.sequence === localeChangeSequence) {
                pendingAxonLocaleRef.current = undefined;
            }
            //  If persisting the locale fails we must not leave the app stranded behind the full-screen
            //  spinner (isUserLocaleChecked stays false and no modal is open). Fall back to the locale
            //  picker so the user can choose/retry instead of an indefinite loading state.
            console.error('Failed to persist user locale', error);
            openLocaleModal();
        }
        finally {
            if (localeChangeSequence === localeChangeSequenceRef.current) {
                setIsNotLoading();
            }
        }
    };
    const checkUserLocale = async () => {
        const legacyStoredLocale = localStorage.getItem(userLocaleKey);
        if (legacyStoredLocale) {
            localStorage.removeItem(userLocaleKey);
        }
        const resolution = resolveStudyLocale({
            siteLocales: siteLocalesList,
            pseudoLocale: storedPseudoLocale,
            statLocaleOverride: activeStatLocaleOverride,
            axonLocale: effectiveAxonLocale,
            sessionLocale,
            rootLocale: globalLocale,
        });
        if (resolution.action === 'prompt') {
            openLocaleModal();
            return;
        }
        // The global profile language wins when supported, but it remains owned by IAM and must not be
        // mirrored to Axon. Automatic single-locale fallbacks likewise remain transient.
        if (toCortexLocale(resolution.locale) === toCortexLocale(currentLocale)) {
            setIsUserLocaleChecked(true);
            return;
        }
        await setUserLocale(resolution.locale);
    };
    useEffect(() => {
        if (environment && axonUser && site) {
            checkUserLocale();
        }
    }, [
        environment,
        axonUser,
        site,
        currentLocale,
        siteLocalesList,
        userLocaleKey,
        storedPseudoLocale,
        activeStatLocaleOverride,
        sessionLocale,
        globalLocale,
    ]);
    // On leaving study routes, restore the nested Sites context and the visible host shell to the
    // independently captured global preference.
    useEffect(() => () => {
        setLocaleRef.current?.(rootShellLocaleRef.current);
        setShellLocaleRef.current?.(rootShellLocaleRef.current);
    }, []);
    const updateUserLocale = async () => {
        const resolvedLocale = selectedLocale ?? selectedLocaleValue;
        if (!environment || !axonUser || !resolvedLocale) {
            return;
        }
        const locale = isPseudoLocale(resolvedLocale.value) ? resolvedLocale.value : toCortexLocale(resolvedLocale.value);
        // Temporarily unmount children to force re-render with new locale
        setIsUserLocaleChecked(false);
        await setUserLocale(locale, true);
        setIsLocaleModalClosed();
    };
    const getUserSelectedLocale = () => {
        const pseudoLocale = getStoredPseudoLocale();
        if (pseudoLocale) {
            return pseudoLocale;
        }
        const resolution = resolveStudyLocale({
            siteLocales: siteLocalesList,
            statLocaleOverride: activeStatLocaleOverride,
            axonLocale: effectiveAxonLocale,
            sessionLocale,
            rootLocale: globalLocale,
        });
        if (resolution.action === 'use') {
            return resolution.locale;
        }
        return resolution.locale ?? currentLocale;
    };
    const getSupportedSelectedLocale = () => {
        const selectedLocaleValue = selectedLocale?.value;
        const selectedSupportedLocale = getSupportedLocale(siteLocalesList, selectedLocaleValue);
        if (selectedSupportedLocale) {
            return formatLocaleOption(selectedSupportedLocale, selectedSupportedLocale);
        }
        const userSelectedLocale = getSupportedLocale(siteLocalesList, getUserSelectedLocale());
        return userSelectedLocale ? formatLocaleOption(userSelectedLocale, userSelectedLocale) : undefined;
    };
    const onModalClose = () => {
        setIsLocaleModalClosed();
        if (!isUserLocaleChecked) {
            navigate('/site/dashboard');
        }
    };
    const openLocaleModal = () => {
        if (!canChangeSiteLocale) {
            return;
        }
        setIsLocaleModalOpen();
    };
    const value = useMemo(() => ({
        isUserLocaleChecked,
        siteLocales: siteLocalesList,
        canChangeSiteLocale,
        isGlobalLocaleSupported,
        openLocaleModal,
        getUserSelectedLocale,
    }), [
        isUserLocaleChecked,
        siteLocalesList,
        canChangeSiteLocale,
        isGlobalLocaleSupported,
        openLocaleModal,
        getUserSelectedLocale,
    ]);
    const selectedLocaleValue = getSupportedSelectedLocale();
    return (_jsxs(UserLocaleContext.Provider, { value: value, children: [(isLoading || !axonUser || !site || (!isUserLocaleChecked && !isLocaleModalOpen)) && _jsx(Spinner, { wrapper: 'full' }), isUserLocaleChecked && children, _jsx(ModalWindow, { title: translate('generic.language'), isOpen: isLocaleModalOpen, closeWindow: () => { }, footerPrimaryActionButton: {
                    label: translate('generic.save'),
                    onClick: updateUserLocale,
                    disabled: !selectedLocaleValue,
                }, footerSecondaryActionButtons: [{ label: translate('generic.cancel'), onClick: onModalClose }], width: 'sm', children: _jsx(LocalesDropdown, { locales: siteLocalesList, label: translate('generic.language'), currentLocale: currentLocale, value: selectedLocaleValue, onChange: (value) => {
                        setSelectedLocale(value);
                    } }) })] }));
};
